/**
 * Contact Section
 * Contact form and business info
 */

import React, { useState } from 'react';
import { usePortal } from '../../context/PortalContext';
import { getApiBaseUrl } from '../../services/api';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const ContactSection = ({ title = 'Get In Touch' }) => {
  const { branding, portal } = usePortal();
  const business = portal?.business;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');

    try {
      const response = await fetch(`${getApiBaseUrl()}/custom-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId: business?.id,
          name: formData.name,
          email: formData.email,
          productDescription: formData.message,
        }),
      });
      if (!response.ok) throw new Error('Failed to send message');
      setStatus('sent');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setStatus('');
      setError(err.message || 'Failed to send message — please try again.');
    }
  };

  return (
    <section className="section section-alt">
      <div className="container">
        <div className="section-heading">
          <h2>{title}</h2>
          <p>We'd love to hear from you</p>
        </div>

        <div className="grid grid-2" style={styles.grid}>
          {/* Contact Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Your Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                style={{ ...styles.input, minHeight: '150px', resize: 'vertical' }}
                required
              />
            </div>

            <button 
              type="submit" 
              style={{ ...styles.submitBtn, background: branding.primaryColor }}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : (
                <>
                  <Send size={18} /> Send Message
                </>
              )}
            </button>

            {status === 'sent' && (
              <p style={styles.success}>Thank you! We'll be in touch soon.</p>
            )}
            {error && <p style={styles.errorText}>{error}</p>}
          </form>

          {/* Contact Info */}
          <div style={styles.info}>
            <h3 style={styles.infoTitle}>Contact Information</h3>

            {business?.email && (
              <div style={styles.infoItem}>
                <Mail size={24} color={branding.primaryColor} />
                <div>
                  <strong>Email</strong>
                  <p>{business.email}</p>
                </div>
              </div>
            )}

            {business?.phone && (
              <div style={styles.infoItem}>
                <Phone size={24} color={branding.primaryColor} />
                <div>
                  <strong>Phone</strong>
                  <p>{business.phone}</p>
                </div>
              </div>
            )}

            {business?.country && (
              <div style={styles.infoItem}>
                <MapPin size={24} color={branding.primaryColor} />
                <div>
                  <strong>Location</strong>
                  <p>{business.country}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  grid: {
    maxWidth: '1000px',
    margin: '0 auto',
  },
  form: {
    background: 'white',
    padding: 'clamp(24px, 5vw, 40px)',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #eee',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s',
    outline: 'none',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '15px',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.3s',
  },
  success: {
    marginTop: '15px',
    color: '#22c55e',
    textAlign: 'center',
    fontWeight: '500',
  },
  errorText: {
    marginTop: '15px',
    color: '#ef4444',
    textAlign: 'center',
    fontWeight: '500',
  },
  info: {
    padding: 'clamp(24px, 5vw, 40px)',
  },
  infoTitle: {
    fontSize: '1.5rem',
    marginBottom: '30px',
    color: '#333',
  },
  infoItem: {
    display: 'flex',
    gap: '15px',
    marginBottom: '25px',
    alignItems: 'flex-start',
  },
};

export default ContactSection;
